import { forwardRef, useCallback, useRef, useState } from 'react';

import { Field } from '@base-ui/react/field';
import { Select as BaseSelect } from '@base-ui/react/select';
import {
  textInputAffixRecipe,
  textInputBoxRecipe,
  textInputHelperRecipe,
  textInputLabelRecipe,
  textInputRootRecipe,
} from 'components/TextInput';
import { useInheritedDarkMode } from 'components/Theme';
import { cn } from 'components/utils';

import {
  selectCaretRecipe,
  selectItemIconRecipe,
  selectItemLabelRecipe,
  selectItemRecipe,
  selectItemTrailingRecipe,
  selectMenuRecipe,
  selectPositionerRecipe,
  selectTriggerRecipe,
  selectValueRecipe,
} from './Select.recipe';

import type { SelectProps } from './types';
import type { SelectRootChangeEventDetails } from '@base-ui/react/select';

/**
 * The caret.
 *
 * Drawn inline rather than taken from an icon library, which is the one place this component departs
 * from the "icons are caller slots" rule the rest of the layer follows. It is not a slot: a select
 * without a caret is not a select, and there is nothing for a caller to decide.
 *
 * TRANSCRIBED from the file's own exported vector rather than approximated, which matters because a
 * hand-drawn approximation is wrong in ways that are hard to see: node I498:41227, whose vector sits
 * at 18.75%/37.5%/18.75%/31.25% of a 16px box - a 10x5 chevron from (3,6) to (13,6) - with the
 * -7.5%/-15% expansion on its wrapper making the stroke 1.5px.
 *
 * `currentColor`, so the recipes own the colour. HNS-262 will likely replace this with a library
 * import; until it lands, the vector is the file's, not invented.
 *
 * The selected row's tick that used to sit beside this is GONE - design feedback on HNS-362 asked for
 * the check on the selected item to be removed. Selected state is now carried by
 * `--ds-select-item-fg-selected` plus the shared row fill, and by the `aria-selected` Base UI sets.
 * Worth knowing before anyone "fixes" the difference: the visual distinction is colour-only, so a
 * colour-blind user is relying on the announced state rather than on a second visual cue. That is the
 * requested trade, not an oversight.
 */
const CaretDownIcon = () => (
  <svg aria-hidden="true" fill="none" focusable="false" viewBox="0 0 16 16">
    <path d="M3 6L8 11L13 6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
  </svg>
);

/**
 * Design System 2.0 single-select.
 *
 * Ported from the Figma `Single select` set (file 5aSkE0U4TeWbuIZDvccHjc, node 498:41207). Props
 * mirror the Figma variant axes where they are real inputs rather than depictions of state:
 * `position` is the `Corner Radius` axis, while `State` and `Content` are observed from the DOM
 * because Base UI's `Field` already tracks them.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer —
 * including for the portalled menu, which gets there via `useInheritedDarkMode`, the same hook
 * `Dialog` uses. See the note on the sentinel below.
 *
 * The closed control is the text input's box, sharing its recipes rather than reproducing its
 * numbers, so a select and a text input stack without a seam. `Select.recipe` explains how, and
 * `TextInputGroup` will stack the two together with no changes of its own.
 *
 * Behaviour worth knowing comes free from Base UI and is the main reason this is not hand-rolled:
 * one tab stop, arrow-key navigation, type-to-move-to-option, focus return to the trigger on close
 * however the list was dismissed, `aria-expanded` on the trigger, and a listbox whose selected row
 * is marked. Those are the parts hand-rolled dropdowns routinely get wrong.
 *
 * Two axes of the design are deliberately absent because the file does not draw them: there is no
 * clear/reset affordance and no required marker. `required` still reaches the form.
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      label,
      labelHidden,
      options,
      position,
      leftIcon,
      helperText,
      error,
      disabled,
      menuFooter,
      'data-testid': testId,
      className,
      rootClassName,
      menuClassName,
      open,
      defaultOpen,
      onOpenChange,
      ...rest
    },
    ref
  ) => {
    const hasHelper = Boolean(helperText);
    const hasLeftIcon = Boolean(leftIcon);

    const boxRef = useRef<HTMLDivElement>(null);

    /**
     * Whether the menu is open — tracked only so `useInheritedDarkMode` can re-resolve the colour
     * scheme each time the portal mounts, which is the one thing that hook needs and Base UI does not
     * hand out. `open` wins when the caller controls it, because a controlled parent can open the
     * select without `onOpenChange` ever firing.
     */
    const [uncontrolledOpen, setUncontrolledOpen] = useState(Boolean(defaultOpen));
    const isOpen = open ?? uncontrolledOpen;

    /**
     * The menu is PORTALLED into `<body>`, so it has none of the trigger's ancestors. The token layer
     * resolves colour from position in the DOM (`.dark`, and the `.ds-select` slot class), which a
     * portal escapes: left alone, a dark page would open a light menu.
     *
     * `useInheritedDarkMode` is the layer's answer to that, and `Dialog` already uses it — the
     * sentinel below marks where this select really lives, and the class comes back to go on the
     * portal. Because the portal is an ANCESTOR of the popup, the popup carries only its own
     * `ds-select` slot class and the ordinary `:where(.dark) .ds-select` rule reaches it, exactly as
     * for a select that never portalled.
     *
     * Re-resolved on every open rather than only on mount, because `.dark` can be added to an
     * ancestor after this component mounts — a Storybook toolbar toggle does exactly that, and so
     * does any page that themes a region on a state change.
     *
     * Portalling is kept rather than avoided: rendering the menu in place would put it inside the
     * field's own box, where an `overflow: hidden` ancestor anywhere up the tree could clip it and
     * a stacking context could bury it.
     */
    const { sentinelRef, inheritedDarkClass } = useInheritedDarkMode(isOpen);

    const handleOpenChange = useCallback(
      (nextOpen: boolean, eventDetails: SelectRootChangeEventDetails) => {
        setUncontrolledOpen(nextOpen);
        onOpenChange?.(nextOpen, eventDetails);
      },
      [onOpenChange]
    );

    return (
      // `invalid` is what puts `data-invalid` on the root and `aria-invalid` on the trigger; every red
      // token keys off it, so the visual and the announced state cannot drift apart.
      <Field.Root
        className={cn(textInputRootRecipe(), 'ds-select', rootClassName)}
        data-testid={testId}
        disabled={disabled}
        invalid={error}
      >
        {/**
         * `items` is what lets `Select.Value` render the chosen option's LABEL in the closed control
         * rather than its raw value. It is fed from the same `options` the list renders, so the two
         * cannot disagree.
         */}
        <BaseSelect.Root
          defaultOpen={defaultOpen}
          disabled={disabled}
          items={options}
          open={open}
          onOpenChange={handleOpenChange}
          {...rest}
        >
          {/* `data-part` names the bordered box so a parent can target it and a test can assert on it
              without depending on a styling class. The slot classes sit on the root. */}
          <div ref={boxRef} className={cn(textInputBoxRecipe({ position, hasHelper }), className)} data-part="box">
            {/* The trigger is the FIRST child and pays all the padding itself, so the button fills the
                box and the entire field opens the menu. Reordering these children, or moving the
                padding onto the box, shrinks the hit area to a 20px strip — see `Select.recipe`. */}
            <BaseSelect.Trigger ref={ref} className={selectTriggerRecipe({ hasHelper, hasLeftIcon, labelHidden })}>
              {leftIcon && <span className={textInputAffixRecipe({ side: 'left' })}>{leftIcon}</span>}

              <span className={selectValueRecipe()}>
                <BaseSelect.Value />
              </span>

              <BaseSelect.Icon className={cn(textInputAffixRecipe({ side: 'right' }), selectCaretRecipe())}>
                <CaretDownIcon />
              </BaseSelect.Icon>
            </BaseSelect.Trigger>

            {/* Hidden rather than absent, exactly as on `TextInputPill`: the trigger still has to be
                named, and a heading sitting above the field is not an accessible name. `sr-only`
                replaces the floating recipe outright rather than joining it, because that recipe
                positions and animates a label this one never shows. */}
            <Field.Label className={labelHidden ? 'sr-only' : textInputLabelRecipe({ hasLeftIcon })}>
              {label}
            </Field.Label>

            {/**
             * Helper text renders inside the border, which is how the design draws the Error cells.
             *
             * `Field.Error` needs `match` to render off a caller-supplied flag — without it, it
             * renders only for Base UI's own constraint validation, which this component does not
             * drive. Either way the text is wired into `aria-describedby`, so it is announced rather
             * than merely visible.
             */}
            {hasHelper && error && (
              <Field.Error className={textInputHelperRecipe()} match>
                {helperText}
              </Field.Error>
            )}
            {hasHelper && !error && (
              <Field.Description className={textInputHelperRecipe()}>{helperText}</Field.Description>
            )}
          </div>

          {/*
            The sentinel. Marks where this select really lives in the React tree so the portal below
            can inherit the ambient colour scheme; `hidden` keeps it out of layout and out of the a11y
            tree. Same shape as `Dialog`'s, which is where this pattern is already proven.
          */}
          <span ref={sentinelRef} hidden />

          <BaseSelect.Portal className={inheritedDarkClass}>
            {/**
             * `alignItemWithTrigger` is Base UI's default and is turned OFF here.
             *
             * On, it slides the panel so the SELECTED row's text lands on the trigger's value text,
             * which means the overlap changes with which option is chosen. The design draws a fixed
             * geometry instead — `top: 49px` on a 60px trigger, so an 11px overlap, and `left/right:
             * -1px` so the borders' outer edges align (node 556:20985). `sideOffset` is negative to
             * reproduce that: 49 - 60.
             *
             * The trade is deliberate. Base UI's default is the nicer pointer behaviour; the fixed
             * offset is what the file specifies and what a visual snapshot can hold still.
             *
             * `anchor` is pointed at the BORDERED BOX rather than left to default to the trigger, and
             * that is what makes the design's numbers land. The trigger sits inside the box's 1px
             * border, so anchoring to it measures 298x58 where the field is 300x60 — every offset
             * would then be derived from the wrong rectangle and would drift again the moment the
             * border width changed. Anchored to the box, `sideOffset={-11}` is exactly the file's
             * `top: 49px` on a 60px field, and the menu's `--anchor-width` is the field's own width.
             */}
            <BaseSelect.Positioner
              align="start"
              alignItemWithTrigger={false}
              alignOffset={-1}
              anchor={boxRef}
              className={selectPositionerRecipe()}
              side="bottom"
              sideOffset={-11}
            >
              {/* `ds-select` is repeated here because the portal escapes the slot class the same way it
                  escapes the theme class. The theme class goes on the PORTAL rather than here, so this
                  element carries one class and the ordinary dark rule matches it. */}
              <BaseSelect.Popup className={cn('ds-select', selectMenuRecipe(), menuClassName)} data-part="menu">
                <BaseSelect.List>
                  {options.map(({ value, label: optionLabel, icon, trailing, disabled: optionDisabled }) => (
                    <BaseSelect.Item key={value} className={selectItemRecipe()} disabled={optionDisabled} value={value}>
                      {icon && <span className={selectItemIconRecipe()}>{icon}</span>}

                      <BaseSelect.ItemText className={selectItemLabelRecipe()}>{optionLabel}</BaseSelect.ItemText>

                      {/* The caller's own trailing cell, INSIDE the row rather than beside it, so it
                          highlights, scrolls and dims with the option it belongs to — see `types.ts`
                          for why this slot exists at all. It is the last thing in the row now that
                          the selected-row indicator is gone. */}
                      {trailing && (
                        <span className={selectItemTrailingRecipe()} data-part="option-trailing">
                          {trailing}
                        </span>
                      )}
                    </BaseSelect.Item>
                  ))}
                </BaseSelect.List>

                {/* Outside `List` on purpose — see the `menuFooter` note in `types.ts`. `data-part`
                    names it so a test can find it without depending on a styling class. */}
                {menuFooter && <div data-part="menu-footer">{menuFooter}</div>}
              </BaseSelect.Popup>
            </BaseSelect.Positioner>
          </BaseSelect.Portal>
        </BaseSelect.Root>
      </Field.Root>
    );
  }
);
